// material-ui
import { Button, Grid, FormHelperText, TextField, Stack, IconButton, Tooltip, Typography, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

// third party
import { Formik, Form, FormikHelpers } from 'formik';
import * as yup from 'yup';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useEffect, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import useReports from 'hooks/useReports.hooks';
import { useCurrentUser } from 'hooks/useUser.hooks';

// ==============================|| DONOR TAX DEDUCTION REPORT PAGE ||============================== //

export interface TaxDeductionReportFormValues {
    startDate: Date | null;
    endDate: Date | null;
    recipientId: number | null;
}

const validationSchema = yup.object({
    startDate: yup
        .date()
        .nullable()
        .required('Start date is required'),
    endDate: yup
        .date()
        .nullable()
        .required('End date is required')
        .when('startDate', (startDate: Date, schema: any) =>
            startDate ? schema.min(startDate, 'End date cannot be before start date') : schema
        ),
    recipientId: yup.number().nullable().required('Recipient is required')
});

const DonorTaxDeductionReportPage = () => {
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [selectedRecipient, setSelectedRecipient] = useState<any | null>(null);
    const [showDetails, setShowDetails] = useState(false);

    const reportsState = useReports();
    const currentUserState = useCurrentUser();

    const currentYear = useMemo(() => new Date().getFullYear(), []);
    const minStartDate = useMemo(() => new Date(currentYear - 1, 0, 1), [currentYear]); // Jan 1 of previous year

    useEffect(() => {
        reportsState.getDonorTaxDeductionRecipientOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (values: TaxDeductionReportFormValues, actions: FormikHelpers<TaxDeductionReportFormValues>) => {
        if (!values.startDate || !values.endDate || !values.recipientId) {
            return;
        }

        const clampedStart =
            values.startDate < minStartDate ? minStartDate : new Date(values.startDate.setHours(0, 0, 0, 0));
        const endDateFormatted = new Date(values.endDate.setHours(23, 59, 59, 999));

        const data: any = {
            startTimestamp: clampedStart.getTime() / 1000,
            endTimestamp: endDateFormatted.getTime() / 1000,
            recipientId: values.recipientId
        };

        const report = await reportsState.getDonorTaxDeductionReport(data);
        const dataURL = URL.createObjectURL(report);

        const link = document.createElement('a');
        link.href = dataURL;
        link.setAttribute('download', `TaxDeductionReport.csv`);

        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);

        if (reportsState.status === 200 && selectedRecipient) {
            setIsSubmitted(true);
        }
    };

    const handleRestart = () => {
        setIsSubmitted(false);
        setSelectedRecipient(null);
    };

    const handleClose = () => {
        setShowDetails(false);
    }

    if (!currentUserState.ready) {
        return <div>Loading</div>;
    }

    const user = currentUserState.user;

    if (user.country !== 'United States') {
        return (
            <MainCard title="Tax Deduction Reports">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        This feature is only available for Food Donors in the United States.
                    </Grid>
                </Grid>
            </MainCard>
        );
    }

    const recipients = reportsState.taxDeductionRecipients || [];

    return (
        <>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            {!isSubmitted ? (
                <MainCard title={
                    <Stack alignItems="center" direction="row" gap={0.8}>
                        <Typography variant="inherit">Generate a Tax Deduction Report</Typography>
                        <IconButton
                            size="small"
                            sx={{ color: 'primary.main' }}
                            onClick={() => {setShowDetails(true)}}
                        >
                            <Tooltip title="How do tax deduction reports work?">
                                <HelpOutlineIcon />
                            </Tooltip>
                        </IconButton>
                    </Stack>
                }>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Formik
                                initialValues={{
                                    startDate: minStartDate,
                                    endDate: new Date(),
                                    recipientId: null
                                }}
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                            >
                                {({ setFieldValue, submitForm, isSubmitting, values, errors, touched }) => (
                                    <Form>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <DatePicker
                                                    label="Start Date"
                                                    value={values.startDate}
                                                    onChange={(newValue: Date | null) => setFieldValue('startDate', newValue)}
                                                    minDate={minStartDate}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                                {touched.startDate && errors.startDate && (
                                                    <FormHelperText error>{errors.startDate as string}</FormHelperText>
                                                )}
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <DatePicker
                                                    label="End Date"
                                                    value={values.endDate}
                                                    onChange={(newValue: Date | null) => setFieldValue('endDate', newValue)}
                                                    minDate={values.startDate || minStartDate}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                                {touched.endDate && errors.endDate && (
                                                    <FormHelperText error>{errors.endDate as string}</FormHelperText>
                                                )}
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Autocomplete
                                                    options={recipients}
                                                    getOptionLabel={(option: any) =>
                                                        `${option.first_name || ''}${
                                                            option.last_name ? ` ${option.last_name}` : ''
                                                        }`
                                                    }
                                                    onChange={(_, newValue: any | null) => {
                                                        setSelectedRecipient(newValue);
                                                        setFieldValue('recipientId', newValue ? newValue.id : null);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Recipient"
                                                            fullWidth
                                                            error={touched.recipientId && Boolean(errors.recipientId)}
                                                            helperText={
                                                                touched.recipientId && errors.recipientId
                                                                    ? (errors.recipientId as string)
                                                                    : ''
                                                            }
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button
                                                    disabled={isSubmitting || !values.startDate || !values.endDate || !values.recipientId}
                                                    type="submit"
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={submitForm}
                                                >
                                                    Generate
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Form>
                                )}
                            </Formik>
                        </Grid>
                    </Grid>
                </MainCard>
            ) : (
                <Grid item xs={12}>
                    <MainCard title="Tax Deduction Report Generated">
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                Your tax deduction report has been generated and will now be downloaded by your browser. If it does
                                not download please ensure you have allowed pop-ups on this site.
                            </Grid>
                            {selectedRecipient && (
                                <Grid item xs={12}>
                                    We are estimating your enhanced tax deductions for donations made to eligible domestic 501(c)(3)
                                    nonprofit organizations based on Feeding America’s standard FMV for donated food. If you have more
                                    accurate data, please use those values. To obtain a signed tax receipt for the donations in this
                                    report, please reach out to{' '}
                                    {`${selectedRecipient.first_name || ''}${
                                        selectedRecipient.last_name ? ` ${selectedRecipient.last_name}` : ''
                                    }`}{' '}
                                    {selectedRecipient.email
                                        ? `at ${selectedRecipient.email}.`
                                        : 'who does not have an email address listed. Please reach out to your Admin to obtain a signed tax receipt.'}
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Button onClick={handleRestart} variant="contained" color="primary">
                                    Generate Another Report
                                </Button>
                            </Grid>
                        </Grid>
                    </MainCard>
                </Grid>
            )}
        </LocalizationProvider>

        <Dialog
            maxWidth="sm"
            open={showDetails}
        >
            <DialogTitle>How do tax deduction reports work?</DialogTitle>
            <DialogContent dividers>
                We are estimating your enhanced tax deductions for donations made to eligible domestic 501(c)(3) nonprofit organizations 
                based on Feeding America’s standard FMV for donated food. If you have more accurate data, please use those values.
                <br/><br/>
                To obtain a signed tax receipt for your donations to an eligible recipient, generate a tax deduction report and then 
                contact the recipient directly using the details provided.
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={handleClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default DonorTaxDeductionReportPage;

